import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  QueryList,
  Renderer2,
  ViewChild,
  inject
} from '@angular/core';
import { StrategyDetailService, Tab } from '@approot/services';
import { clone } from '@approot/utilities';
import { LegComponent } from '../leg/leg.component';

@Component({
  selector: 'app-strategy-detail-tabs',
  templateUrl: './strategy-detail-tabs.component.html',
  styleUrls: ['./strategy-detail-tabs.component.scss']
})
export class StrategyDetailTabsComponent implements AfterViewInit, OnDestroy {
  private strategyDetailService = inject(StrategyDetailService);
  public readonly tabsState = this.strategyDetailService.tabsState;
  @Output() public changeInSelectedTab = new EventEmitter<Tab>();
  @Input() public legComponentsQueryList: QueryList<LegComponent>;
  public warningScreenData = {
    tabIndex: null,
    warningScreenOpen: false,
    messageToDisplay: '',
    headerMessage: ''
  };
  private renderer = inject(Renderer2);
  private tabNameInterval: any;

  public ngAfterViewInit(): void {
    this.tabNameUpdatePoller();
  }

  public ngOnDestroy(): void {
    clearInterval(this.tabNameInterval);
  }

  @ViewChild('divToScroll', { static: false }) private divToScroll: ElementRef;

  public addNewTab(): void {
    this.tabsState.update(state => {
      const clonedState = clone(state);
      clonedState.tabs.push(clone(this.strategyDetailService.defaultTab));
      return clonedState;
    });

    this.onTabSwitch(this.tabsState().tabs.length - 1);

    setTimeout(() => {
      this.scrollToLastTab();
    }, 0);
  }

  public onTabSwitch(index: number, updateLegs = true): void {
    if (updateLegs && index === this.tabsState().selectedTab) {
      return;
    }
    this.strategyDetailService.onTabSwitch(index, updateLegs);
    this.changeInSelectedTab.emit(this.strategyDetailService.currentTab);
  }

  public onCloseTab(index: number): void {
    const tabsState = this.tabsState();
    const tabToClose = tabsState.tabs[index];
    const isCurrentTab = tabsState.selectedTab === index;

    if (
      (!isCurrentTab && tabToClose.legs.length === 0) ||
      (isCurrentTab && !this.strategyDetailService.currentLegComponents.length)
    ) {
      this.closeTab(index);
      return;
    }
    this.warningScreenData.headerMessage = 'Close all legs on tab?';
    this.warningScreenData.messageToDisplay = `Are you sure you want to close all legs relating to tab: ${tabToClose.name}?`;
    this.warningScreenData.tabIndex = index;
    this.warningScreenData.warningScreenOpen = true;
  }

  public shouldTabBeClosed(shouldClose: boolean): void {
    this.warningScreenData.warningScreenOpen = false;
    if (shouldClose) {
      this.closeTab(this.warningScreenData.tabIndex);
    }
  }

  public closeTab(index: number): void {
    const currentTabDeleted = index === this.tabsState().selectedTab;
    this.tabsState.update(state => {
      const clonedState = clone(state);
      clonedState.tabs.splice(index, 1);
      if (index < clonedState.selectedTab) {
        clonedState.selectedTab = clonedState.selectedTab - 1;
      } else if (
        currentTabDeleted &&
        !clonedState.tabs[clonedState.selectedTab] &&
        clonedState.tabs[clonedState.selectedTab - 1]
      ) {
        clonedState.selectedTab = clonedState.selectedTab - 1;
      }
      return clonedState;
    });

    if (currentTabDeleted) {
      this.onTabSwitch(this.tabsState().selectedTab, false);
    }
  }

  public onTabRename(event: Event, index: number): void {
    const elem = event.target;
    const nameToUse = elem['innerText'].trim() || 'Unnamed Tab';
    this.renderer.setProperty(elem, 'innerText', nameToUse);
    this.tabsState.update(state => {
      const clonedState = clone(state);
      clonedState.tabs[index].name = nameToUse;
      return clonedState;
    });
  }

  private scrollToLastTab(): void {
    let e = this.divToScroll.nativeElement;
    this.renderer.setProperty(e, 'scrollLeft', e.scrollWidth);
  }

  private tabNameUpdatePoller() {
    this.tabNameInterval = setInterval(() => {
      const legs = this.legComponentsQueryList.toArray();
      if (!legs.length) {
        return;
      }
      const form = legs[0].form;
      const cross = form.get('cross');
      const crossVal = cross?.value || '';
      const productType = form.get('productType');
      const tabsState = this.tabsState;
      const index = tabsState().selectedTab;
      const currentName = tabsState().tabs[index].name;
      if (
        productType &&
        (!cross || crossVal) &&
        currentName === this.strategyDetailService.defaultTab.name
      ) {
        tabsState.update(state => {
          const clonedState = clone(state);
          clonedState.tabs[index].name =
            `${crossVal} ${productType.value}`.trim();
          return clonedState;
        });
      }
    }, 1000);
  }
}
